import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  resetOnRouteChange?: boolean;
  label?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const label = this.props.label ?? 'ErrorBoundary';
    console.error(`[${label}] Uncaught render error`, { error, componentStack: info.componentStack });
  }

  reset = () => this.setState({ hasError: false, error: null });

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.props.fallback) return this.props.fallback;

    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[200px] p-6 bg-surface-1 rounded-lg border border-surface-3">
        <div className="text-center max-w-sm">
          <div className="text-red-400 text-2xl mb-3">⚠</div>
          <p className="text-text-primary font-medium mb-1">Something went wrong</p>
          {this.state.error?.message && (
            <p className="text-text-muted text-sm mb-4 font-mono break-all">
              {this.state.error.message}
            </p>
          )}
          <button
            onClick={this.reset}
            className="px-4 py-2 bg-accent text-white text-sm rounded hover:bg-accent/80 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
}
